import { ApplicationRef, EmbeddedViewRef, EnvironmentInjector, Injectable, createComponent } from '@angular/core';
import type { Cell, Worksheet, Workbook } from 'exceljs';
import { AssayWorkbookExportRendererComponent } from '../assay-workbook-export-renderer/assay-workbook-export-renderer.component';
import { extractTableFromHtml, ExtractedTable, ExtractedTableCell, ExtractedTableRow } from '../utils/assay-workbook-table-builder';
import { ExportWorkbookStatusService } from '../../../shared/services/export-workbook-status.service';

type ExcelCellStyle = {
  alignment?: {
    horizontal?: 'left' | 'center' | 'right';
    vertical?: 'top' | 'middle' | 'bottom';
  };
  font?: {
    bold?: boolean;
    color?: { argb: string };
  };
  fill?: {
    type: 'pattern';
    pattern: 'solid';
    fgColor: { argb: string };
  };
  border?: {
    top?: { style: 'thin' | 'medium' };
    left?: { style: 'thin' | 'medium' };
    right?: { style: 'thin' | 'medium' };
    bottom?: { style: 'thin' | 'medium' };
  };
};

@Injectable({ providedIn: 'root' })
export class AssayWorkbookExportService {
  private readonly summaryTableId = 'assay-summary-export-table';
  private readonly detailsTableId = 'assay-details-export-table';

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    private exportWorkbookStatus: ExportWorkbookStatusService
  ) {}

  async exportWorkbook(options?: { truncateSummary?: boolean }): Promise<void> {
    this.exportWorkbookStatus.start();
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.overflow = 'hidden';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    const componentRef = createComponent(AssayWorkbookExportRendererComponent, {
      environmentInjector: this.injector
    });
    componentRef.instance.summaryTableId = this.summaryTableId;
    componentRef.instance.detailsTableId = this.detailsTableId;

    this.appRef.attachView(componentRef.hostView);
    const rootNode = (componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    container.appendChild(rootNode);
    componentRef.changeDetectorRef.detectChanges();

    try {
      const tables = await this.waitForTables(container, [this.summaryTableId, this.detailsTableId]);
      const summaryTable = tables.get(this.summaryTableId);
      const detailsTable = tables.get(this.detailsTableId);
      if (!summaryTable || !detailsTable) {
        throw new Error('Assay tables could not be rendered for export.');
      }

      const summaryExtracted = extractTableFromHtml(summaryTable);
      const detailsExtracted = extractTableFromHtml(detailsTable);
      const processedSummaryTable = options?.truncateSummary
        ? this.getTruncatedSummaryTable(summaryExtracted)
        : summaryExtracted;

      const ExcelJSImport = await import('exceljs');
      const WorkbookCtor =
        (ExcelJSImport as { Workbook?: new () => Workbook }).Workbook ??
        (ExcelJSImport as { default?: { Workbook?: new () => Workbook } }).default?.Workbook;
      if (!WorkbookCtor) {
        throw new Error('ExcelJS Workbook constructor not found.');
      }
      const workbook = new WorkbookCtor();
      const summarySheet = workbook.addWorksheet('assaysummary');
      const detailsSheet = workbook.addWorksheet('assaydetails');

      this.populateWorksheet(summarySheet, processedSummaryTable);
      this.populateWorksheet(detailsSheet, detailsExtracted);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      this.triggerDownload(blob, 'assays-export.xlsx');
    } finally {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
      container.remove();
      this.exportWorkbookStatus.stop();
    }
  }

  private async waitForTables(container: HTMLElement, ids: string[]): Promise<Map<string, HTMLTableElement>> {
    const timeoutMs = 4000;
    const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const start = now();
    return new Promise((resolve) => {
      const check = () => {
        const map = new Map<string, HTMLTableElement>();
        ids.forEach(id => {
          const table = container.querySelector(`#${id}`) as HTMLTableElement | null;
          if (table) {
            map.set(id, table);
          }
        });
        const allFound = ids.every(id => map.has(id) && (map.get(id)?.rows.length ?? 0) > 0);
        if (allFound || now() - start > timeoutMs) {
          resolve(map);
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  private populateWorksheet(sheet: Worksheet, table: ExtractedTable): void {
    const columnWidths: number[] = [];
    let rowIndex = 1;

    table.rows.forEach(row => {
      let colIndex = 1;
      row.cells.forEach(cell => {
        const startCol = colIndex;
        const endCol = colIndex + cell.colSpan - 1;
        if (cell.colSpan > 1) {
          sheet.mergeCells(rowIndex, startCol, rowIndex, endCol);
        }
        for (let col = startCol; col <= endCol; col += 1) {
          const excelCell = sheet.getCell(rowIndex, col);
          if (col === startCol) {
            excelCell.value = this.getWorkbookCellValue(cell, row);
          }
          this.applyCellStyle(excelCell, cell, row);
          const textLength = String(cell.value ?? '').length;
          columnWidths[col - 1] = Math.max(columnWidths[col - 1] ?? 0, textLength);
        }
        colIndex = endCol + 1;
      });
      rowIndex += 1;
    });

    columnWidths.forEach((width, index) => {
      sheet.getColumn(index + 1).width = Math.min(60, Math.max(8, width + 2));
    });
  }

  private getTruncatedSummaryTable(table: ExtractedTable): ExtractedTable {
    const normalizeLabel = (value: string): string =>
      value
        .replace(/\u00a0/g, ' ')
        .replace(/[–—]/g, '-')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    const excludedSummaryRows = new Set<string>([
      '# RVs Discarded: RV and Assay Errors',
      '# RVs Discarded: Bead Count Errors',
      '# RVs Discarded: Cal Failed',
      '# RVs Discarded: Outlier',
      '# RVs Analyzed'
    ].map(normalizeLabel));
    const allowedRangeRows = new Set<string>([
      'Slope',
      'Intercept',
      'Correlation',
      'Bias Type',
      'Mean',
      'Limit of Agreement P/F/A',
      'Low Limits of Agreement',
      'High Limits of Agreement',
      'Pass/Fail Spec'
    ].map(normalizeLabel));
    const excludedRangeRows = new Set<string>([
      '95% CI of slope',
      'Pass/Fail/Alert',
      'Alert Spec.',
      'Pass/Fail Spec.',
      '95% CI of intercept',
      'SEE',
      'Correlation Coefficient R2',
      '95% CI of Mean Bias',
      'Standard Deviation',
      'Standard Error (SE)',
      '1.96*STD',
      '1.96*SE',
      'DF',
      'T-Statistic',
      'T-Critical',
      'P'
    ].map(normalizeLabel));

    const rows: ExtractedTableRow[] = [];
    let insideControlTestData = false;
    let insideRangeSection = false;

    table.rows.forEach(row => {
      const firstCellValue = row.cells[0]?.value?.trim() ?? '';
      const normalizedFirstCell = normalizeLabel(firstCellValue);
      const isSectionHeader = this.isSectionHeaderRow(row);
      const isRangeHeader = /^range\s+\d+\s*-/.test(normalizedFirstCell);
      const isControlTestHeader = /^control test data\s*-/.test(normalizedFirstCell);

      if (isSectionHeader) {
        if (isControlTestHeader) {
          insideControlTestData = true;
          insideRangeSection = false;
          return;
        }

        if (isRangeHeader) {
          insideRangeSection = true;
          insideControlTestData = false;
          rows.push(row);
          return;
        }

        insideControlTestData = false;
        insideRangeSection = false;
        rows.push(row);
        return;
      }

      if (insideControlTestData) {
        return;
      }

      if (excludedSummaryRows.has(normalizedFirstCell)) {
        return;
      }

      if (excludedRangeRows.has(normalizedFirstCell)) {
        return;
      }

      if (insideRangeSection && !allowedRangeRows.has(normalizedFirstCell)) {
        return;
      }

      rows.push(row);
    });

    return { rows };
  }

  private applyCellStyle(cell: Cell, cellMeta: ExtractedTableCell, rowMeta: ExtractedTableRow): void {
    const style: ExcelCellStyle = {};
    const isSectionHeader = this.isSectionHeaderRow(rowMeta);

    style.alignment = {
      horizontal: isSectionHeader ? 'left' : (cellMeta.isFirstColumn ? 'right' : 'left'),
      vertical: 'middle'
    };

    const fontStyle: NonNullable<ExcelCellStyle['font']> = {};
    if (cellMeta.isHeaderCell || cellMeta.isFirstColumn || isSectionHeader) {
      fontStyle.bold = true;
    }
    // For section headers we use rich text so only the status token is colorized.
    if (cellMeta.textColorArgb && !isSectionHeader) {
      fontStyle.color = { argb: cellMeta.textColorArgb };
    }
    if (Object.keys(fontStyle).length > 0) {
      style.font = fontStyle;
    }

    if (isSectionHeader) {
      style.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD0D0D0' }
      };
      style.border = {
        top: { style: 'medium' },
        bottom: { style: 'medium' }
      };
    }

    if (cellMeta.classes.includes('selected-kit-highlight')) {
      style.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFF59D' }
      };
    }

    if (cellMeta.classes.includes('adjusted-column-highlight')) {
      style.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE3F2FD' }
      };
    }

    if (cellMeta.classes.includes('final-column-highlight')) {
      style.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFECE6F5' }
      };
    }

    if (cellMeta.classes.includes('mean-column-border')) {
      style.border = {
        ...(style.border ?? {}),
        left: { style: 'medium' }
      };
    }
  
    style.border = {
      top: style.border?.top ?? { style: 'thin' },
      bottom: style.border?.bottom ?? { style: 'thin' },
      left: style.border?.left ?? { style: 'thin' },
      right: style.border?.right ?? { style: 'thin' }
    };

    cell.style = style;
  }

  private getWorkbookCellValue(cellMeta: ExtractedTableCell, rowMeta: ExtractedTableRow): Cell['value'] {
    const rawValue = cellMeta.value;
    if (!this.isSectionHeaderRow(rowMeta)) {
      return rawValue;
    }

    const match = rawValue.match(/^(.*-\s*)(PASS|FAIL|ALERT|NA)\s*$/i);
    if (!match) {
      return rawValue;
    }

    const prefix = match[1];
    const status = match[2].toUpperCase();
    const statusArgbByValue: Record<string, string> = {
      PASS: 'FF008000',
      FAIL: 'FFFF0000',
      ALERT: 'FFFFA500',
      NA: 'FF000000'
    };
    const statusArgb = statusArgbByValue[status] ?? 'FF000000';

    return {
      richText: [
        { text: prefix },
        { text: status, font: { color: { argb: statusArgb } } }
      ]
    };
  }

  private isSectionHeaderRow(row: ExtractedTableRow): boolean {
    const classes = row.rowClasses;
    return classes.includes('subsection-header') ||
      classes.includes('subsection-header-print') ||
      classes.includes('subsection-header-print2') ||
      classes.includes('subsection-header-print-firstrow');
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
